import { useState } from 'preact/hooks';
import { SendIcon } from 'lucide-react';
import { Button } from '@/components/button.js';

/** Each arm carries its own handler, so a handler on the wrong arm is a type error. */
export type ComposerSpec =
  | { kind: 'continue'; label: string; onContinue: () => void | Promise<void> }
  | { kind: 'yes-no'; onYes: () => void | Promise<void>; onNo: () => void | Promise<void> }
  | {
      kind: 'input';
      placeholder?: string;
      inputMode?: 'numeric' | 'text';
      validate?: (value: string) => string | null;
      onSubmit: (value: string) => void | Promise<void>;
    }
  | {
      // Running totals belong to the header cart, which is the one place the order is
      // described — `itemCount` only decides whether this button can be pressed.
      kind: 'checkout';
      itemCount: number;
      onCheckout: () => void | Promise<void>;
    };

interface ComposerProps {
  spec: ComposerSpec;
}

/** Each sub-component takes the arm it renders, already narrowed by `Composer`. */
type VariantProps<K extends ComposerSpec['kind']> = {
  spec: Extract<ComposerSpec, { kind: K }>;
};

export function Composer({ spec }: ComposerProps) {
  return (
    <div className="sm-stack sm-chat-composer">
      <div className="sm-stack sm-chat-composer-inner">
        {spec.kind === 'continue' ? (
          <Button onClick={() => void spec.onContinue()}>{spec.label}</Button>
        ) : spec.kind === 'yes-no' ? (
          <YesNoVariant spec={spec} />
        ) : spec.kind === 'input' ? (
          <InputVariant spec={spec} />
        ) : (
          <CheckoutVariant spec={spec} />
        )}
      </div>
    </div>
  );
}

function CheckoutVariant({ spec }: VariantProps<'checkout'>) {
  const hasItems = spec.itemCount > 0;
  return (
    <Button
      onClick={() => void spec.onCheckout()}
      disabled={!hasItems}
      endIcon={hasItems ? undefined : null}
    >
      {hasItems ? 'Continue to checkout' : 'Choose a plan to continue'}
    </Button>
  );
}

function YesNoVariant({ spec }: VariantProps<'yes-no'>) {
  return (
    <div className="sm-row-2-stack">
      <button type="button" className="sm-control sm-chip" onClick={() => void spec.onNo()}>
        No
      </button>
      <button
        type="button"
        className="sm-control sm-chip"
        data-tone="primary"
        onClick={() => void spec.onYes()}
      >
        Yes
      </button>
    </div>
  );
}

function InputVariant({ spec }: VariantProps<'input'>) {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  function handleSubmit(e?: Event) {
    e?.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) {
      setError('Please enter a value.');
      return;
    }
    const validateErr = spec.validate ? spec.validate(trimmed) : null;
    if (validateErr) {
      setError(validateErr);
      return;
    }
    setError(null);
    void spec.onSubmit(trimmed);
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="sm-control sm-chat-input-row">
        <input
          className="sm-control sm-tabular sm-chat-input"
          type="text"
          inputMode={spec.inputMode ?? 'text'}
          placeholder={spec.placeholder ?? 'Type your reply…'}
          value={value}
          onInput={(e) => {
            setValue((e.currentTarget as HTMLInputElement).value);
            if (error) setError(null);
          }}
          autoFocus
        />
        <button
          type="submit"
          className="sm-center sm-focus-ring sm-chat-send"
          aria-label="Send"
          disabled={value.trim().length === 0}
        >
          <SendIcon size={18} aria-hidden="true" />
        </button>
      </div>
      {error ? <p className="sm-type-body sm-chat-composer-error">{error}</p> : null}
    </form>
  );
}
